// utils/llmGrouping.js
// Groups the account's LLM models by capability for the model pickers in the
// workflow builders. The signal is the backend `cost_tier` (1..5), which the
// /llms endpoint already returns. cost_tier ties capability to price, so the
// group labels double as a cost hint for non-technical users.
//
// Returns an array of groups shaped for both StyledSelect (grouped `options`)
// and a native <select> with <optgroup>:
//   [{ label: '⚡ Fast & economical · 1 coin', options: [{ value, label }] }]
// Empty groups are dropped, so a tier with no enabled model never shows.

const TIER_GROUPS = [
  { tiers: [1, 2], label: "⚡ Fast & economical", coins: 1 },
  { tiers: [3], label: "⚖️ Balanced", coins: 3 },
  { tiers: [4], label: "🧠 Advanced", coins: 5 },
  { tiers: [5], label: "🚀 Most capable", coins: 10 },
];

const modelLabel = (m) => m.model_name || m.modelName || m.name || m.id;

// The /llms endpoint serializes in camelCase (BaseSchema alias_generator),
// so the field arrives as `costTier`. Fall back to snake_case defensively.
const modelTier = (m) => m.costTier ?? m.cost_tier;

const coinSuffix = (coins) => `${coins} coin${coins === 1 ? "" : "s"}`;

export function groupLlmsByCapability(llmModels = []) {
  const models = Array.isArray(llmModels) ? llmModels : [];

  const toOption = (m) => ({ value: m.id, label: modelLabel(m) });
  const byLabel = (a, b) => a.label.localeCompare(b.label);

  const groups = TIER_GROUPS.map((g) => ({
    label: `${g.label} · ${coinSuffix(g.coins)}`,
    options: models
      .filter((m) => g.tiers.includes(modelTier(m)))
      .map(toOption)
      .sort(byLabel),
  })).filter((g) => g.options.length > 0);

  // Models with a missing/unmapped tier still need to be selectable.
  const mappedTiers = new Set(TIER_GROUPS.flatMap((g) => g.tiers));
  const other = models
    .filter((m) => !mappedTiers.has(modelTier(m)))
    .map(toOption)
    .sort(byLabel);
  if (other.length) groups.push({ label: "Other", options: other });

  return groups;
}

// Clean vendor names derived from the provider_model_id prefix
// (e.g. "openai/gpt-4o" → "OpenAI"). The customer thinks in vendors,
// not in our routing provider (everything routes through OpenRouter).
const PROVIDER_LABELS = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  deepseek: "DeepSeek",
  moonshotai: "Moonshot AI",
  qwen: "Qwen",
  "z-ai": "Z.ai",
  "x-ai": "xAI",
  "meta-llama": "Meta",
  mistralai: "Mistral",
  cohere: "Cohere",
};

export function groupLlmsByProvider(llmModels = []) {
  const models = Array.isArray(llmModels) ? llmModels : [];

  const toOption = (m) => ({ value: m.id, label: modelLabel(m) });
  const byLabel = (a, b) => a.label.localeCompare(b.label);
  const providerOf = (m) => {
    const prefix = String(m.provider_model_id || m.providerModelId || "").split("/")[0];
    return PROVIDER_LABELS[prefix] || m.brand || "Other";
  };

  const groupsMap = new Map();
  for (const m of models) {
    const provider = providerOf(m);
    if (!groupsMap.has(provider)) groupsMap.set(provider, []);
    groupsMap.get(provider).push(toOption(m));
  }

  return [...groupsMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([label, options]) => ({ label, options: options.sort(byLabel) }));
}
